import React, { useEffect, useState } from 'react';
import { useAdminUser } from './useAdminUser';
import AdminLogin from './AdminLogin';
import { AlertTriangle, Loader2, LogOut, User } from 'lucide-react';

/**
 * AdminAuthProvider - Wraps admin routes with simple password authentication
 * 
 * This component:
 * 1. Checks if user is authenticated via localStorage
 * 2. Shows login screen if not authenticated  
 * 3. Renders children if authenticated and has admin access
 * 4. Provides clean admin header with user info and logout
 */
const AdminAuthProvider = ({ children }) => {
  const { user, logout, loading, isAuthenticated, hasAdminAccess } = useAdminUser();
  const [initializing, setInitializing] = useState(true);

  // Handle initial authentication check
  useEffect(() => {
    if (!loading) {
      setInitializing(false);
    }
  }, [loading]);

  // Show loading spinner during initial auth check
  if (initializing || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" aria-live="polite">
        <div className="text-center" role="status" aria-label="Checking authentication">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-base text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  // Check if user has admin access
  if (!hasAdminAccess()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-base text-muted-foreground mb-4">
            You don&apos;t have permission to access admin features. Please check your credentials.
          </p>
          <button 
            onClick={() => window.location.href = '/'}
            className="inline-flex items-center h-11 px-4 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Main App
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and has admin access - render admin interface
  return (
    <div className="admin-interface">
      {/* Admin header with user info and logout */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {user.firstName ? `${user.firstName} ${user.lastName}` : user.email}
              </p>
              <p className="text-xs text-gray-500">Admin Access</p>
            </div>
          </div>
          
          <button
            onClick={logout}
            className="inline-flex items-center h-11 px-4 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </button>
        </div>
      </div>
      
      {/* Admin content */}
      <div className="admin-content">
        {children}
      </div>
    </div>
  );
};

export default AdminAuthProvider;